import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Business as BusinessIcon,
    NotificationsActive as NotificationsActiveIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';

const Block = ({ title, subtitle, children, action = null }) => (
    <Card sx={{ borderRadius: 2, height: '100%' }}>
        <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
                    {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
                </Box>
                {action}
            </Box>
            <Divider sx={{ mb: 1.5 }} />
            {children}
        </CardContent>
    </Card>
);

const DemoAlarmDetailPage = () => {
  const { t } = useTranslation();
    const { companyId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [detail, setDetail] = useState(null);

    const load = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await api.admin.getDemoDetail(companyId);
            setDetail(response?.data || null);
        } catch (err) {
            setError(err?.response?.data?.detail || err?.message || t('hardcoded.pages_demoalarmdetailpage.no_fue_posible_cargar_el'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [companyId]);

    const status = detail?.status || {};
    const notifications = detail?.notifications || {};

    const healthColor = useMemo(() => {
        if (!status.total_alarm_events) return 'warning';
        return 'success';
    }, [status.total_alarm_events]);

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
                    <Stack spacing={1} alignItems="center">
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary">{t('hardcoded.pages_demoalarmdetailpage.cargando_detalle_de_la_demo')}</Typography>
                    </Stack>
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Stack spacing={2}>
                    <Alert severity="error">{error}</Alert>
                    <Stack direction="row" spacing={1}>
                        <Button startIcon={<ArrowBackIcon />} variant="outlined" onClick={() => navigate('/vehicles/schematics')}>
                            {i18n.t('hardcoded.components_operators_operatorquickcreateform.volver')}</Button>
                        <Button startIcon={<RefreshIcon />} variant="contained" onClick={load}>
                            {i18n.t('hardcoded.components_ai_airecommendationspanel.reintentar')}</Button>
                    </Stack>
                </Stack>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 900 }}>
                            {t('hardcoded.pages_demoalarmdetailpage.demo_de_alarmas_arsenal_-')}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('hardcoded.pages_demoalarmdetailpage.vista_consolidada_del_recorrido_guiado')}</Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/vehicles/schematics')}>
                            {i18n.t('hardcoded.pages_demoalarmdetailpage.volver_a_schematic')}</Button>
                        <Button variant="contained" startIcon={<RefreshIcon />} onClick={load}>
                            {i18n.t('audit.actions.UPDATE')}</Button>
                    </Stack>
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                        <Block
                            title={detail?.company?.name || t('hardcoded.pages_demoalarmdetailpage.empresa_demo')}
                            subtitle={`ID: ${detail?.company?.id || 'N/A'}`}
                            action={<BusinessIcon color="primary" />}
                        >
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                                <Chip label={t('hardcoded.pages_demoalarmdetailpage.vehiculos_statustotal_veh', { statustotal_veh: status.total_vehicles || 0 })} color="primary" />
                                <Chip label={t('hardcoded.pages_demoalarmdetailpage.receptores_statustotal_rec', { statustotal_rec: status.total_receivers || 0 })} color="info" />
                                <Chip label={t('hardcoded.pages_demoalarmdetailpage.sensores_statustotal_sen', { statustotal_sen: status.total_sensors || 0 })} color="secondary" />
                                <Chip label={t('hardcoded.pages_demoalarmdetailpage.perfiles_statustotal_ala', { statustotal_ala: status.total_alarm_profiles || 0 })} color="warning" />
                                <Chip label={t('hardcoded.pages_demoalarmdetailpage.eventos_statustotal_ala', { statustotal_ala: status.total_alarm_events || 0 })} color={healthColor} />
                            </Stack>
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2">Email manager: {detail?.company?.manager_email || 'N/A'}</Typography>
                                <Typography variant="body2">{i18n.t('hardcoded.pages_demoalarmdetailpage.telefono')}{detail?.company?.phone || 'N/A'}</Typography>
                                <Typography variant="body2">{i18n.t('hardcoded.pages_demoalarmdetailpage.creada')}{detail?.company?.created_at || 'N/A'}</Typography>
                            </Box>
                        </Block>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Block
                            title={t('navbar.notifications')}
                            subtitle={t('hardcoded.pages_demoalarmdetailpage.estado_por_canal')}
                            action={<NotificationsActiveIcon color="success" />}
                        >
                            <List dense>
                                {['email', 'whatsapp', t('hardcoded.pages_chatpage.call')].map((channel) => (
                                    <ListItem key={channel} disableGutters>
                                        <ListItemText
                                            primary={channel.toUpperCase()}
                                            secondary={t('hardcoded.pages_demoalarmdetailpage.planificadas_notificationsch_enviadas_notificationsch', { notificationsch: notifications?.[channel]?.planned || 0, notificationsch: notifications?.[channel]?.sent || 0 })}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Block>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Block title={t('hardcoded.pages_demoalarmdetailpage.vehiculos')} subtitle={t('hardcoded.pages_demoalarmdetailpage.unidades_creadas_durante_la_demo')}>
                            <List dense sx={{ maxHeight: 240, overflow: 'auto' }}>
                                {(detail?.vehicles || []).map((vehicle) => (
                                    <ListItem key={vehicle.id} disableGutters>
                                        <ListItemText
                                            primary={vehicle.display_name || vehicle.alias || 'Vehiculo'}
                                            secondary={t('hardcoded.pages_demoalarmdetailpage.id_id_tipo_vehicletypena_posiciones', { id: vehicle.id, vehicletypeNA: vehicle.type || 'N/A', vehicleposition: vehicle.positions_count || 0 })}
                                        />
                                    </ListItem>
                                ))}
                                {(detail?.vehicles || []).length === 0 && (
                                    <Typography variant="body2" color="text.secondary">{t('hardcoded.pages_demoalarmdetailpage.sin_vehiculos_en_esta_demo')}</Typography>
                                )}
                            </List>
                        </Block>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Block title={t('hardcoded.pages_demoalarmdetailpage.receptores_y_sensores')} subtitle={t('hardcoded.pages_demoalarmdetailpage.provision_y_asignaciones')}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{i18n.t('receivers.title')}</Typography>
                            <List dense sx={{ maxHeight: 120, overflow: 'auto', mb: 1 }}>
                                {(detail?.receivers || []).map((receiver) => (
                                    <ListItem key={receiver.id} disableGutters>
                                        <ListItemText
                                            primary={receiver.mac_address}
                                            secondary={t('hardcoded.pages_demoalarmdetailpage.dynamo_id_dynamo_receiver_id_vehiculo_receivervehicle', { dynamo_receiver_id: receiver.dynamo_receiver_id, receivervehicle: receiver.vehicle_id || t('sensors.stats.unassigned') })}
                                        />
                                    </ListItem>
                                ))}
                            </List>

                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{i18n.t('sensors.title')}</Typography>
                            <List dense sx={{ maxHeight: 120, overflow: 'auto' }}>
                                {(detail?.sensors || []).map((sensor) => (
                                    <ListItem key={sensor.id} disableGutters>
                                        <ListItemText
                                            primary={`${sensor.sensor_id}`}
                                            secondary={`${sensor.description || t('hardcoded.pages_demoalarmdetailpage.sin_descripcion')} | ID: ${sensor.id}`}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Block>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Block title={t('hardcoded.pages_demoalarmdetailpage.perfiles_y_reglas')} subtitle={t('hardcoded.pages_demoalarmdetailpage.configuracion_de_alarmas_aplicada')}>
                            <List dense sx={{ maxHeight: 240, overflow: 'auto' }}>
                                {(detail?.alarm_profiles || []).map((profile) => (
                                    <ListItem key={profile.id} disableGutters>
                                        <ListItemText
                                            primary={profile.name}
                                            secondary={t('hardcoded.pages_demoalarmdetailpage.id_id_ejes_axle_count_activo', { id: profile.id, axle_count: profile.axle_count, profileactiveSi: profile.active ? 'Si' : t('common.no') })}
                                        />
                                    </ListItem>
                                ))}
                                {(detail?.alarm_profiles || []).length === 0 && (
                                    <Typography variant="body2" color="text.secondary">{t('hardcoded.pages_demoalarmdetailpage.sin_perfiles_de_alarma')}</Typography>
                                )}
                            </List>
                        </Block>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Block title={t('hardcoded.pages_demoalarmdetailpage.eventos_de_alarma')} subtitle={t('hardcoded.pages_demoalarmdetailpage.eventos_generados_durante_la_simulacion')}>
                            <List dense sx={{ maxHeight: 240, overflow: 'auto' }}>
                                {(detail?.alarm_events || []).map((event) => (
                                    <ListItem key={event.id} disableGutters>
                                        <ListItemText
                                            primary={`${event.breach_type} - ${event.metric_type}`}
                                            secondary={t('hardcoded.pages_demoalarmdetailpage.sensor_eventsensor_idn_posicion_eventposition_valor', { eventsensor_idN: event.sensor_id || 'N/A', eventposition: event.position || '-', eventvalue: event.value || '-', eventthreshold_: event.threshold_min || '-', eventthreshold_: event.threshold_max || '-', status: event.status })}
                                        />
                                    </ListItem>
                                ))}
                                {(detail?.alarm_events || []).length === 0 && (
                                    <Typography variant="body2" color="text.secondary">{t('hardcoded.pages_alarmticketdetailpage.sin_eventos_registrados')}</Typography>
                                )}
                            </List>
                        </Block>
                    </Grid>
                </Grid>
            </Stack>
        </Container>
    );
};

export default DemoAlarmDetailPage;
